import { z } from 'zod';
import {
  Email,
  IsoDateTime,
  NonEmptyName,
  Pence,
  Phone,
  SignedPence,
  Uuid,
} from '../primitives.js';

/**
 * Stock contract (Phase 4). Entity/response schemas carry `.meta({ id })`; query schemas must NOT.
 * Money is integer pence. On-hand is computed (SUM of qty_delta), never a stored column.
 */

// ── Enums ────────────────────────────────────────────────────────────────────
export const ProductKind = z.enum(['retail', 'professional']);
export type ProductKind = z.infer<typeof ProductKind>;

export const StockMovementKind = z.enum(['receive', 'sell', 'waste', 'count_correction', 'return']);
export type StockMovementKind = z.infer<typeof StockMovementKind>;

// ── Suppliers ──────────────────────────────────────────────────────────────────
export const Supplier = z
  .object({
    id: Uuid,
    salonId: Uuid,
    name: NonEmptyName,
    contactEmail: Email.nullable(),
    contactPhone: Phone.nullable(),
    accountRef: z.string().nullable(),
    notes: z.string().max(2000).nullable(),
    active: z.boolean(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'Supplier' });
export type Supplier = z.infer<typeof Supplier>;

export const CreateSupplier = z
  .object({
    name: NonEmptyName,
    contactEmail: Email.nullish(),
    contactPhone: Phone.nullish(),
    accountRef: z.string().max(80).nullish(),
    notes: z.string().max(2000).nullish(),
  })
  .meta({ id: 'CreateSupplier' });
export type CreateSupplier = z.infer<typeof CreateSupplier>;

export const UpdateSupplier = CreateSupplier.partial()
  .extend({ active: z.boolean().optional() })
  .meta({ id: 'UpdateSupplier' });
export type UpdateSupplier = z.infer<typeof UpdateSupplier>;

// ── Products ───────────────────────────────────────────────────────────────────
export const Product = z
  .object({
    id: Uuid,
    salonId: Uuid,
    supplierId: Uuid.nullable(),
    name: NonEmptyName,
    kind: ProductKind,
    sku: z.string().max(64).nullable(),
    barcode: z.string().max(64).nullable(),
    costPence: Pence,
    pricePence: Pence,
    vatable: z.boolean(),
    lowStockThreshold: z.int().nonnegative(),
    trackStock: z.boolean(),
    active: z.boolean(),
    onHand: z.int().describe('computed SUM(qty_delta); may be negative if oversold'),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'Product' });
export type Product = z.infer<typeof Product>;

export const CreateProduct = z
  .object({
    name: NonEmptyName,
    kind: ProductKind.default('retail'),
    supplierId: Uuid.nullish(),
    sku: z.string().max(64).nullish(),
    barcode: z.string().max(64).nullish(),
    costPence: Pence.default(0),
    pricePence: Pence.default(0),
    vatable: z.boolean().default(true),
    lowStockThreshold: z.int().nonnegative().default(0),
    trackStock: z.boolean().default(true),
    openingStock: z.int().nonnegative().default(0),
  })
  .meta({ id: 'CreateProduct' });
export type CreateProduct = z.infer<typeof CreateProduct>;

export const UpdateProduct = CreateProduct.omit({ openingStock: true })
  .partial()
  .extend({ active: z.boolean().optional() })
  .meta({ id: 'UpdateProduct' });
export type UpdateProduct = z.infer<typeof UpdateProduct>;

// ── Movements ──────────────────────────────────────────────────────────────────
export const StockMovement = z
  .object({
    id: Uuid,
    salonId: Uuid,
    productId: Uuid,
    kind: StockMovementKind,
    qtyDelta: z.int(),
    unitCostPence: Pence.nullable(),
    unitPricePence: Pence.nullable(),
    supplierId: Uuid.nullable(),
    paymentId: Uuid.nullable(),
    receiptLineItemId: Uuid.nullable(),
    soldByStaffId: Uuid.nullable(),
    renterId: Uuid.nullable(),
    reason: z.string().max(500).nullable(),
    createdBy: Uuid.nullable(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'StockMovement' });
export type StockMovement = z.infer<typeof StockMovement>;

export const CreateStockMovement = z
  .object({
    productId: Uuid,
    kind: z.enum(['receive', 'waste', 'count_correction', 'return']),
    qtyDelta: z.int().refine((n) => n !== 0, 'qtyDelta must be non-zero'),
    unitCostPence: Pence.nullish(),
    supplierId: Uuid.nullish(),
    reason: z.string().max(500).nullish(),
    idempotencyKey: z.string().min(8).max(200).optional(),
  })
  .meta({ id: 'CreateStockMovement' });
export type CreateStockMovement = z.infer<typeof CreateStockMovement>;

// ── Levels + low-stock event ──────────────────────────────────────────────────
export const StockLevel = z
  .object({
    productId: Uuid,
    name: NonEmptyName,
    kind: ProductKind,
    onHand: z.int(),
    lowStockThreshold: z.int().nonnegative(),
    isLow: z.boolean(),
    costPence: Pence,
    stockValuePence: SignedPence,
  })
  .meta({ id: 'StockLevel' });
export type StockLevel = z.infer<typeof StockLevel>;

export const StockLowEvent = z
  .object({
    productId: Uuid,
    name: NonEmptyName,
    onHand: z.int(),
    lowStockThreshold: z.int().nonnegative(),
    at: IsoDateTime,
  })
  .meta({ id: 'StockLowEvent' });
export type StockLowEvent = z.infer<typeof StockLowEvent>;

// ── Query schemas (NO .meta — query/param gotcha) ───────────────────────────────
export const ProductListQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
  q: z.string().max(120).optional(),
  kind: ProductKind.optional(),
  barcode: z.string().max(64).optional(),
  lowStockOnly: z.coerce.boolean().optional(),
  active: z.coerce.boolean().optional(),
});
export type ProductListQuery = z.infer<typeof ProductListQuery>;

export const StockMovementListQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
  productId: Uuid.optional(),
  kind: StockMovementKind.optional(),
  from: IsoDateTime.optional(),
  to: IsoDateTime.optional(),
});
export type StockMovementListQuery = z.infer<typeof StockMovementListQuery>;
